import {
  isPointInPolygon,
  isPointWithinRadius,
  getGreatCircleBearing,
  computeDestinationPoint,
} from 'geolib'
import { GPSLocation, Polygon } from '../../model/GeolocationNode'
import { AisLocation } from '../aislocationService/AisLocation'
import { nauticalMilesToMeters } from './distanceConverter'

export class Position {
  private _latitude: number
  private _longitude: number
  private _timestamp?: number | null

  constructor(
    latitude: number,
    longitude: number,
    timestamp: number | null = null
  ) {
    this._latitude = latitude
    this._longitude = longitude
    this._timestamp = timestamp
  }

  static fromGeolocationPosition(position: GeolocationPosition) {
    return new Position(
      position.coords.latitude,
      position.coords.longitude,
      position.timestamp
    )
  }

  static fromGPSLocation(location: GPSLocation) {
    return new Position(location.lat!, location.long!)
  }

  static fromAislocation(aisLocation: AisLocation) {
    const {
      coords: { latitude: lat, longitude: lon },
      timestamp,
    } = aisLocation
    return new Position(lat, lon, timestamp)
  }

  static polygonToArray(polygon: Polygon) {
    return polygon.coords.map((coord) => new Position(coord[1], coord[0]))
  }

  get latitude() {
    return this._latitude
  }

  get longitude() {
    return this._longitude
  }

  get timestamp() {
    return this._timestamp
  }

  get toGeolibInputCoordinates() {
    return {
      latitude: this.latitude,
      longitude: this.longitude,
    }
  }

  getLocationPrediction(speed: number, course: number, time: number): Position {
    if (!this._timestamp) {
      throw new Error('Current position timestamp is not set')
    }

    const MAX_PREDICTION_TIME = 3600000 // 1 hour in milliseconds
    if (time - this._timestamp > MAX_PREDICTION_TIME) {
      console.warn('Location prediction beyond 1 hour may be inaccurate')
    }

    if (speed < 0 || speed > 102.3) {
      throw new Error('Invalid speed value. Must be between 0 and 102.3 knots.')
    }

    if (course < 0 || course > 360) {
      throw new Error(
        'Invalid course value. Must be between 0 and 360.0 degrees.'
      )
    }

    if (speed === 102.3 || course === 360) {
      throw new Error('Speed or course is not available.')
    }

    const timeElapsedInHours = (time - this._timestamp) / 3600000
    const distanceTraveled = speed * timeElapsedInHours

    const distanceInMeters = nauticalMilesToMeters(distanceTraveled)

    const destinationPoint = computeDestinationPoint(
      this.toGeolibInputCoordinates,
      distanceInMeters,
      course
    )

    return new Position(
      destinationPoint.latitude,
      destinationPoint.longitude,
      time
    )
  }

  isPositionInCircle(center: Position, radius: number) {
    return isPointWithinRadius(
      this.toGeolibInputCoordinates,
      center.toGeolibInputCoordinates,
      radius
    )
  }

  isPositionInPolygon(polygon: Position[]) {
    return isPointInPolygon(
      this.toGeolibInputCoordinates,
      polygon.map((p) => p.toGeolibInputCoordinates)
    )
  }

  isPositionRelative(
    relativePosition: Position,
    distance: number,
    direction?: { min: number; max: number }
  ) {
    if (this.isPositionInCircle(relativePosition, distance)) return false
    if (!direction) return true

    const angle = getGreatCircleBearing(
      relativePosition.toGeolibInputCoordinates,
      this.toGeolibInputCoordinates
    )
    return angle >= direction.min && angle <= direction.max
  }

  isSameLocation(position: Position) {
    return (
      this.latitude === position.latitude &&
      this.longitude === position.longitude
    )
  }

  isThisTheLatestPosition(previousPosition: Position) {
    return (
      this.timestamp !== undefined &&
      this.timestamp !== null &&
      previousPosition.timestamp !== undefined &&
      previousPosition.timestamp !== null &&
      this.timestamp > previousPosition.timestamp
    )
  }
}
